import { Link } from "react-router";
import { LuPencil } from "react-icons/lu";
import ListTableRegion from "../../common/ListTableRegion";
import { NETWORK_MANAGER_ROUTE_PATHS } from "../../common/constants";
import {
    formatNetworkCellValue,
    formatNetworkResultsFoundLabel,
    getNetworkStatusTone,
} from "../../../../utils/planManager/networkConfigurationUtils.js";
import "./MainContent.scss";

/**
 * Sortable table column metadata, in display order. Edit is not sortable.
 * @readonly
 * @type {ReadonlyArray<{ field: string | null, label: string }>}
 */
export const NETWORK_TABLE_COLUMNS = Object.freeze([
    { field: "clientName", label: "Client Name" },
    { field: "clientId", label: "Client ID" },
    { field: "oeUrlName", label: "Pre-Enrollment URL name" },
    { field: "status", label: "Status" },
    { field: null, label: "Edit" },
]);

const NETWORK_TABLE_COLUMN_DEFS = NETWORK_TABLE_COLUMNS.map((column) => ({
    key: column.label,
    label: column.label,
    sortable: Boolean(column.field),
    field: column.field,
}));

/**
 * Networks list table and optional search-result count. Pure presentational —
 * emits user intent upward via callbacks; owns no state.
 *
 * @param {object} props
 * @param {Array<Object>} props.networkConfigs - Filtered and sorted rows to display.
 * @param {string} props.searchCriteria - Current search text; count is shown when non-empty.
 * @param {string | null} props.sortedField - Active sort column field, or null when unsorted.
 * @param {boolean} props.isSortDescending - Whether the active sort is descending.
 * @param {(field: string) => void} props.onSort - Cycles sort for a sortable column field.
 * @returns {import('react').JSX.Element}
 */
const MainContent = ({
    networkConfigs,
    searchCriteria,
    sortedField,
    isSortDescending,
    onSort,
}) => {
    const isSearchNetworks = Boolean(searchCriteria);
    const isEmpty = !networkConfigs || networkConfigs.length === 0;
    const tableClassName = isEmpty
        ? "networks-table is-empty"
        : "networks-table";

    return (
        <ListTableRegion
            resultsLabel={
                isSearchNetworks
                    ? formatNetworkResultsFoundLabel(networkConfigs.length)
                    : undefined
            }
            tableClassName={tableClassName}
            columnCount={NETWORK_TABLE_COLUMNS.length}
            isEmpty={isEmpty}
            columns={NETWORK_TABLE_COLUMN_DEFS}
            sortedField={sortedField}
            isSortDescending={isSortDescending}
            onSort={onSort}
            sortButtonClassName="networks-table__sort-button"
        >
            {networkConfigs.map((row) => (
                <tr key={row.id}>
                    <td>{formatNetworkCellValue(row.clientName)}</td>
                    <td>{formatNetworkCellValue(row.clientId)}</td>
                    <td>{formatNetworkCellValue(row.oeUrlName)}</td>
                    <td>
                        <span
                            className={`networks-table__status networks-table__status--${getNetworkStatusTone(
                                row.status,
                            )}`}
                        >
                            {formatNetworkCellValue(row.status)}
                        </span>
                    </td>
                    <td>
                        <Link
                            to={`${NETWORK_MANAGER_ROUTE_PATHS.NETWORKS_CONFIG}/${encodeURIComponent(
                                row.clientName,
                            )}`}
                            className="networks-table__action-btn networks-table__action-btn--edit"
                            title="Edit"
                            aria-label="Edit"
                            data-hover="Edit"
                        >
                            <LuPencil aria-hidden size={16} />
                        </Link>
                    </td>
                </tr>
            ))}
        </ListTableRegion>
    );
};

export default MainContent;
